import React from 'react';
import Chip, { type ChipProps } from '@mui/material/Chip';
import { useTheme } from '../../context/ThemeContext';

export type StatusChipVariant = 'success' | 'warning' | 'error' | 'info';

export interface StatusChipProps extends Omit<ChipProps, 'variant' | 'color'> {
  status: string;
  variant?: StatusChipVariant;
}

const variantToStatusKey: Record<StatusChipVariant, string> = {
  success: 'ready',
  warning: 'pending',
  error: 'cancelled',
  info: 'accepted',
};

const fallbackColors: Record<StatusChipVariant, { bg: string; text: string; border: string }> = {
  success: { bg: 'rgba(16, 185, 129, 0.13)', text: '#1E8E5A', border: 'rgba(16, 185, 129, 0.3)' },
  warning: { bg: 'rgba(245, 158, 11, 0.13)', text: '#E0891E', border: 'rgba(245, 158, 11, 0.3)' },
  error: { bg: 'rgba(239, 68, 68, 0.13)', text: '#D2453C', border: 'rgba(239, 68, 68, 0.3)' },
  info: { bg: 'rgba(29, 78, 216, 0.13)', text: '#2D6CB5', border: 'rgba(29, 78, 216, 0.3)' },
};

const StatusChip: React.FC<StatusChipProps> = ({
  status,
  variant = 'info',
  sx,
  ...props
}) => {
  const { theme } = useTheme();
  const statusKey = variantToStatusKey[variant];
  const themeStatus = theme.components?.status?.[statusKey];
  const colors = themeStatus ?? fallbackColors[variant];

  return (
    <Chip
      label={status}
      data-testid={`status-chip-${variant}`}
      sx={{
        backgroundColor: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border ?? 'transparent'}`,
        fontWeight: 600,
        fontSize: '0.75rem',
        height: 28,
        ...sx,
      }}
      {...props}
    />
  );
};

export default StatusChip;
